import type { TrafficRelatedBy, TrafficRelatedRow, TrafficTopDimension } from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { getTrafficRelated, type TrafficScope } from '../api/traffic.js';
import { formatPercent } from '../lib/format.js';
import { botLabel } from '../lib/traffic.js';
import { RELATIONS, type Relation } from '../lib/traffic-related.js';
import { AddressLink } from './AddressStatus.js';
import { Flag } from './Flag.js';

/**
 * What is joined to a top-list row, shown under it (P11.13): the addresses that
 * asked for a page, the pages an address asked for, how each was answered, which
 * bot. The lists are the heaviest combinations the agents report, not a log, and the
 * panel says so when a list comes back short.
 *
 * Fetched when the row is opened, not before: a page of fifty rows opens none.
 */

const STATUS_WORDS: Readonly<Record<string, string>> = {
  '200': 'OK',
  '301': 'Moved',
  '302': 'Redirect',
  '304': 'Not modified',
  '400': 'Bad request',
  '401': 'Unauthorised',
  '403': 'Forbidden',
  '404': 'Not found',
  '405': 'Method not allowed',
  '429': 'Too many requests',
  '500': 'Server error',
  '502': 'Bad gateway',
  '503': 'Unavailable',
};

const count = (value: number): string => value.toLocaleString('en-US');

function Entry({
  show,
  row,
  addressAction,
}: {
  show: TrafficRelatedBy;
  row: TrafficRelatedRow;
  addressAction: ((ip: string) => ReactNode) | undefined;
}): ReactNode {
  const refused = row.requests === 0 ? 0 : (row.status4xx + row.status5xx) / row.requests;
  return (
    <li className="related-row">
      <span className="related-name">
        {show === 'ip' && (
          <>
            <AddressLink ip={row.key} />
            <span className="cell-sub">
              <Flag code={row.geo?.country ?? null} />
              {row.geo?.network != null && ` ${row.geo.network}`}
            </span>
          </>
        )}
        {show === 'path' && <code>{row.key}</code>}
        {show === 'bot' && botLabel(row.key)}
        {show === 'status' && (
          <>
            <strong>{row.key}</strong>
            {STATUS_WORDS[row.key] !== undefined && (
              <span className="cell-sub">{STATUS_WORDS[row.key]}</span>
            )}
          </>
        )}
      </span>
      <span className="related-count">
        {count(row.requests)}
        {show !== 'status' && refused > 0 && (
          <span className="cell-sub">{formatPercent(refused * 100, 0)} refused</span>
        )}
      </span>
      {show === 'ip' && addressAction !== undefined && (
        <span className="related-action">{addressAction(row.key)}</span>
      )}
    </li>
  );
}

function List({
  scope,
  by,
  keyValue,
  relation,
  addressAction,
}: {
  scope: TrafficScope;
  by: TrafficRelatedBy;
  keyValue: string;
  relation: Relation;
  addressAction: ((ip: string) => ReactNode) | undefined;
}): ReactNode {
  const related = useQuery({
    queryKey: ['traffic-related', scope, by, keyValue, relation.show],
    queryFn: () => getTrafficRelated(scope, by, keyValue, relation.show),
    staleTime: 30_000,
  });
  return (
    <section>
      <h4>{relation.title}</h4>
      {related.isPending && (
        <div className="skeleton skeleton-block" aria-busy="true" aria-label="Loading" />
      )}
      {related.isError && <p className="muted text-sm">Could not load this.</p>}
      {related.data !== undefined &&
        (related.data.rows.length === 0 ? (
          <p
            className="muted text-sm"
            title="Each server reports the heaviest combinations of the minute, not a log; a server on an older agent reports none, and an address is kept 14 days."
          >
            None reported in this range.
          </p>
        ) : (
          <ol className="related-list">
            {related.data.rows.map((row) => (
              <Entry key={row.key} show={relation.show} row={row} addressAction={addressAction} />
            ))}
          </ol>
        ))}
    </section>
  );
}

export function TrafficRelated({
  dimension,
  keyValue,
  scope,
  addressAction,
}: {
  dimension: TrafficTopDimension;
  keyValue: string;
  scope: TrafficScope;
  addressAction?: ((ip: string) => ReactNode) | undefined;
}): ReactNode {
  const plan = RELATIONS[dimension];
  if (plan === undefined) return null;
  return (
    <>
      {plan.shows.map((relation) => (
        <List
          key={relation.show}
          scope={scope}
          by={plan.by}
          keyValue={keyValue}
          relation={relation}
          addressAction={addressAction}
        />
      ))}
    </>
  );
}
